import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  TicketIcon,
  StarIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { publicAPI, bookingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Services = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [selectedType, setSelectedType] = useState('pack');
  const [services, setServices] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [detailService, setDetailService] = useState(null);

  // Fetch services and client services from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const servicesData = await publicAPI.getServices();
        setServices(Array.isArray(servicesData) ? servicesData : (servicesData?.results || []));

        // Fetch client services if authenticated
        if (isAuthenticated) {
          try {
            const clientServicesData = await bookingAPI.getMyClientServices();
            setClientServices(clientServicesData.results || clientServicesData);
          } catch (error) {
            console.error('Error fetching client services:', error);
          }
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des services');
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  // Helper functions
  const hasActiveService = () => {
    return clientServices.some(service => service.status === 'active');
  };

  const handleSelectService = (service) => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour acheter un service');
      navigate('/login');
      return;
    }

    // Allow multiple active services - no restriction
    setSelectedService(service);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    navigate('/book-service/confirmation', {
      state: {
        service: selectedService,
        user: user
      }
    });
  };

  // Filter services by type
  const filteredServices = Array.isArray(services) ? services.filter(service => service.type === selectedType) : [];

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des services...</p>
        </div>
      </div>
    );
  }





  const currentServices = filteredServices;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Services & Tarifs
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choisissez la formule qui correspond à vos objectifs et votre rythme d'entraînement.
            </p>
          </div>
        </div>
      </section>

      {/* Service Type Selector */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="bg-white rounded-lg p-1 shadow-sm border border-gray-200">
              <button
                onClick={() => setSelectedType('pack')}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  selectedType === 'pack'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Packs
              </button>
              <button
                onClick={() => setSelectedType('abonnement')}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  selectedType === 'abonnement'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Abonnements
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Explanation */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
            <div className="flex items-start">
              <InformationCircleIcon className="h-6 w-6 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary-900 mb-2">
                  {selectedType === 'pack' ? 'Comment fonctionnent les Packs ?' : 'Comment fonctionnent les Abonnements ?'}
                </h3>
                <p className="text-primary-800">
                  {selectedType === 'pack' 
                    ? 'Les packs vous donnent un nombre fixe de séances à utiliser dans une période définie. Idéal pour une flexibilité totale sans contrainte mensuelle.'
                    : 'Les abonnements vous donnent un quota de séances chaque mois. Les séances non utilisées ne se reportent pas au mois suivant.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentServices.map((service) => {
              // Determine if this is a popular service (you can customize this logic)
              const isPopular = service.name.toLowerCase().includes('premium') || service.name.toLowerCase().includes('standard');

              // Calculate duration display
              const duration = service.type === 'pack'
                ? `${service.duration_days} jours`
                : `${service.duration_months} mois`;

              // Generate features based on service data
              const features = [
                `${service.session_count === 999 ? 'Séances illimitées' : `${service.session_count} séances`}`,
                service.type === 'pack' ? `Valable ${service.duration_days} jours` : 'Renouvellement mensuel',
                'Choix du coach et studio',
                'Équipement inclus',
                'Suivi personnalisé'
              ];

              return (
                <div
                  key={service.id}
                  className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-200 hover:scale-105 ${
                    isPopular ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  {isPopular && (
                    <div className="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 text-sm font-medium">
                      Populaire
                    </div>
                  )}

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-gray-600 mb-1 line-clamp-1">{service.description}</p>
                    <button
                      type="button"
                      onClick={() => setDetailService(service)}
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
                      aria-label="Voir les détails du service"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                      Voir les détails
                    </button>

                    <div className="flex items-baseline mb-6">
                      <span className="text-4xl font-bold text-gray-900">{service.price}</span>
                      <span className="text-xl text-gray-600 ml-1">DH</span>
                      {selectedType === 'abonnement' && (
                        <span className="text-gray-600 ml-2">/mois</span>
                      )}
                    </div>

                    <div className="flex items-center mb-6 space-x-4">
                      <div className="flex items-center text-gray-600">
                        <TicketIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm">
                          {service.session_count === 999 ? 'Illimité' : `${service.session_count} séances`}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <ClockIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm">{duration}</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Multiple Services Info */}
                    {isAuthenticated && hasActiveService() && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-sm text-green-800">Vous pouvez avoir plusieurs services actifs</span>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => handleSelectService(service)}
                      disabled={false}
                      className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 flex items-center justify-center transform hover:scale-105 bg-primary-600 hover:bg-primary-700 text-white"
                    >
                      {!isAuthenticated
                        ? 'Se connecter pour acheter'
                        : 'Choisir ce service'
                      }
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <ClockIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Horaires flexibles</h3>
              <p className="text-gray-600">Réservez vos séances de 8h00 à 21h00, 7 jours sur 7</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Paiement sécurisé</h3>
              <p className="text-gray-600">Transactions sécurisées et factures automatiques</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CalendarDaysIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Annulation gratuite</h3>
              <p className="text-gray-600">Annulez ou reprogrammez jusqu'à 24h avant la séance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {detailService && (
        <ServiceDetailModal
          service={detailService}
          selectedType={selectedType}
          onClose={() => setDetailService(null)}
          onSelectService={() => {
            setDetailService(null);
            handleSelectService(detailService);
          }}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedService && (
        <PaymentModal
          service={selectedService}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

// Service Detail Modal – full description and info
const ServiceDetailModal = ({ service, selectedType, onClose, onSelectService, isAuthenticated }) => {
  const duration = service.type === 'pack'
    ? `${service.duration_days} jours`
    : `${service.duration_months} mois`;
  const features = [
    `${service.session_count === 999 ? 'Séances illimitées' : `${service.session_count} séances`}`,
    service.type === 'pack' ? `Valable ${service.duration_days} jours` : 'Renouvellement mensuel',
    'Choix du coach et studio',
    'Équipement inclus',
    'Suivi personnalisé'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Détails du service</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Fermer"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.name}</h3>
          <p className="text-gray-600 whitespace-pre-wrap mb-6">{service.description}</p>
          <div className="flex items-baseline mb-4">
            <span className="text-3xl font-bold text-gray-900">{service.price}</span>
            <span className="text-lg text-gray-600 ml-1">DH</span>
            {selectedType === 'abonnement' && (
              <span className="text-gray-600 ml-2">/mois</span>
            )}
          </div>
          <div className="flex items-center mb-6 space-x-4 text-gray-600">
            <span className="flex items-center">
              <TicketIcon className="h-5 w-5 mr-2" />
              {service.session_count === 999 ? 'Illimité' : `${service.session_count} séances`}
            </span>
            <span className="flex items-center">
              <ClockIcon className="h-5 w-5 mr-2" />
              {duration}
            </span>
          </div>
          <ul className="space-y-2 mb-8">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={onSelectService}
              className="flex-1 py-3 px-4 rounded-lg font-medium bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center"
            >
              {!isAuthenticated ? 'Se connecter pour acheter' : 'Choisir ce service'}
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ service, onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(false);
  // Default to cash for web, card for mobile apps
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: ''
  });

  // Check if running on web (hide card payment on web)
  const isWeb = true;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Create client service via API
      const serviceData = {
        service_type: service.id,
        payment_method: paymentMethod
      };

      await bookingAPI.createClientService(serviceData);

      if (paymentMethod === 'cash') {
        toast.success('Commande confirmée ! Payez en espèces au studio.');
      } else {
        toast.success('Paiement réussi ! Service activé.');
      }
      onSuccess();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Finaliser l'achat</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Service Summary */}
        <div className="bg-gray-50 p-6 border-b border-gray-200">
          <h3 className="font-medium text-gray-900 mb-2">{service.name}</h3>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{service.session_count} séances</span>
            <span className="text-xl font-bold text-primary-600">{service.price} MAD</span>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handlePayment} className="p-6">
          {/* Payment Method Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Méthode de paiement</h3>

            <div className={`grid gap-3 ${isWeb ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {/* Hide card payment on web */}
              {!isWeb && (
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    paymentMethod === 'card'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CreditCardIcon className="h-6 w-6 mx-auto mb-1" />
                  <span className="text-sm font-medium">Carte bancaire</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`p-3 border rounded-lg text-center transition-colors ${
                  paymentMethod === 'cash'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <CurrencyDollarIcon className="h-6 w-6 mx-auto mb-1" />
                <span className="text-sm font-medium">Espèces</span>
              </button>
            </div>
          </div>

          {paymentMethod === 'card' && (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2" />
                Informations de paiement
              </h3>

              <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom sur la carte
              </label>
              <input
                type="text"
                name="cardName"
                value={paymentData.cardName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de carte
              </label>
              <input
                type="text"
                name="cardNumber"
                value={paymentData.cardNumber}
                onChange={handleInputChange}
                required
                maxLength="19"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="1234 5678 9012 3456"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date d'expiration
                </label>
                <input
                  type="text"
                  name="expiryDate"
                  value={paymentData.expiryDate}
                  onChange={handleInputChange}
                  required
                  maxLength="5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="MM/YY"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVV
                </label>
                <input
                  type="text"
                  name="cvv"
                  value={paymentData.cvv}
                  onChange={handleInputChange}
                  required
                  maxLength="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123"
                />
              </div>
            </div>
          </div>
          </>
          )}

          {paymentMethod === 'cash' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-yellow-800 mb-2">
                    Paiement en espèces
                  </h3>
                  <p className="text-sm text-yellow-700 mb-3">
                    Votre commande sera confirmée et vous pourrez effectuer le paiement en espèces directement au studio lors de votre première séance.
                  </p>
                  <div className="bg-yellow-100 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 font-medium">
                      📍 Adresse de paiement : Nazaï Studio
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Veuillez vous présenter 15 minutes avant votre première séance pour effectuer le règlement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={processing}
            className="w-full mt-6 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Traitement en cours...
              </div>
            ) : (
              paymentMethod === 'cash'
                ? `Commander ${service.price} MAD (Paiement en espèces)`
                : `Payer ${service.price} MAD`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Services;
